import { useModalLifecycle } from '../shared/useModalLifecycle'
import React, { useState } from 'react'
import { X, UserCheck, AlertTriangle, RotateCcw, Search } from 'lucide-react'
import { LineupSlot, RosterAthleteOption } from '../../types/sports'

interface QuickSubstitutionModalProps {
  isOpen: boolean
  onClose: () => void
  unit: 'offense' | 'defense'
  slotPosition: string
  currentSlot: LineupSlot
  allRosterAthletes: RosterAthleteOption[]
  activeOffenseSlots: LineupSlot[]
  activeDefenseSlots: LineupSlot[]
  onConfirmSubstitution: (params: {
    newAthlete: RosterAthleteOption
    scope: 'this_snap' | 'following_snaps'
    reason?: string
  }) => void
  onResetToTemplate?: () => void
  isCustomLineup?: boolean
}

export const QuickSubstitutionModal: React.FC<QuickSubstitutionModalProps> = ({
  isOpen,
  onClose,
  unit,
  slotPosition,
  currentSlot,
  allRosterAthletes = [],
  activeOffenseSlots = [],
  activeDefenseSlots = [],
  onConfirmSubstitution,
  onResetToTemplate,
  isCustomLineup,
}) => {
  const [selectedAthleteId, setSelectedAthleteId] = useState<string>('')
  const [scope, setScope] = useState<'this_snap' | 'following_snaps'>('this_snap')
  const [reason, setReason] = useState<string>('Rotación')
  const [customReason, setCustomReason] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState<string>('')

  useModalLifecycle(isOpen, onClose)

  if (!isOpen) return null

  // Jugadores activos en la misma unidad
  const sameUnitAthleteIds = (unit === 'offense' ? activeOffenseSlots : activeDefenseSlots)
    .filter((s) => s.position !== slotPosition)
    .map((s) => s.athlete_id)

  // Jugadores activos en la unidad contraria (para detectar doble presencia en enfrentamientos)
  const opposingUnitAthleteIds = (unit === 'offense' ? activeDefenseSlots : activeOffenseSlots).map(
    (s) => s.athlete_id
  )

  const filteredAthletes = allRosterAthletes.filter((a) => {
    if (!searchFilter.trim()) return true
    const term = searchFilter.toLowerCase()
    return (
      a.full_name.toLowerCase().includes(term) ||
      (a.dorsal_number && a.dorsal_number.toString().includes(term)) ||
      (a.primary_position && a.primary_position.toLowerCase().includes(term))
    )
  })

  const selectedAthlete = allRosterAthletes.find((a) => a.id === selectedAthleteId)
  const isSelectedInOpposing = selectedAthleteId
    ? opposingUnitAthleteIds.includes(selectedAthleteId)
    : false

  const handleConfirm = () => {
    if (!selectedAthlete) return
    const finalReason = reason === 'Otro' ? customReason.trim() : reason
    onConfirmSubstitution({
      newAthlete: selectedAthlete,
      scope,
      reason: finalReason || undefined,
    })
    onClose()
  }

  const commonReasons = ['Rotación', 'Molestia física', 'Táctica / Matchup', 'Enseñanza', 'Otro']

  return (
    <div
      className="mobile-viewport-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.7)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 1100,
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        className="mobile-viewport-sheet"
        style={{
          width: '100%',
          maxWidth: '520px',
          backgroundColor: 'var(--surface-card)',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          padding: '1.25rem',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.25)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          borderTop: '2px solid var(--brand-red)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            borderBottom: '1px solid var(--surface-border)',
            paddingBottom: '0.75rem',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span
                className={`badge ${unit === 'offense' ? 'badge-red' : 'badge-neutral'}`}
                style={{ fontSize: '0.72rem', textTransform: 'uppercase' }}
              >
                {unit === 'offense' ? 'Ataque' : 'Defensa'} · Posición: {slotPosition}
              </span>
              {isCustomLineup && (
                <span
                  style={{
                    fontSize: '0.68rem',
                    color: 'var(--brand-yellow)',
                    fontWeight: 600,
                    backgroundColor: 'var(--brand-yellow-subtle)',
                    padding: '0.15rem 0.4rem',
                    borderRadius: '4px',
                  }}
                >
                  Alineación modificada
                </span>
              )}
            </div>
            <h3
              style={{
                fontSize: '1.1rem',
                fontWeight: 700,
                color: 'var(--text-main)',
                margin: '0.25rem 0 0',
              }}
            >
              Sustituir a {currentSlot.athlete_name} {currentSlot.dorsal_number ? `#${currentSlot.dorsal_number}` : ''}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '0.4rem',
            }}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Alcance del cambio */}
        <div
          style={{
            backgroundColor: 'var(--surface-subtle)',
            padding: '0.75rem',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--surface-border)',
            marginBottom: '0.85rem',
          }}
        >
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
            ALCANCE DE LA SUSTITUCIÓN
          </span>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.35rem' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: scope === 'this_snap' ? 'var(--brand-red)' : 'var(--text-main)',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="sub_scope"
                checked={scope === 'this_snap'}
                onChange={() => setScope('this_snap')}
              />
              Solo para este snap
            </label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: scope === 'following_snaps' ? 'var(--brand-red)' : 'var(--text-main)',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                name="sub_scope"
                checked={scope === 'following_snaps'}
                onChange={() => setScope('following_snaps')}
              />
              Mantener en siguientes snaps
            </label>
          </div>
        </div>

        {/* Motivo de la sustitución */}
        <div style={{ marginBottom: '0.85rem' }}>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
            MOTIVO (TRAZABILIDAD)
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.3rem' }}>
            {commonReasons.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setReason(r)}
                style={{
                  fontSize: '0.74rem',
                  padding: '0.25rem 0.55rem',
                  borderRadius: '4px',
                  border: reason === r ? '1px solid var(--brand-red)' : '1px solid var(--surface-border)',
                  backgroundColor: reason === r ? 'var(--brand-red-subtle)' : 'var(--surface-card)',
                  color: reason === r ? 'var(--brand-red)' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontWeight: reason === r ? 700 : 500,
                }}
              >
                {r}
              </button>
            ))}
          </div>
          {reason === 'Otro' && (
            <input
              type="text"
              placeholder="Especificar motivo breve..."
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              className="form-input"
              style={{ marginTop: '0.4rem', fontSize: '0.8rem', padding: '0.35rem 0.6rem' }}
            />
          )}
        </div>

        {/* Buscador de jugadores */}
        <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
          <Search
            size={14}
            style={{
              position: 'absolute',
              left: '0.65rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-muted)',
            }}
          />
          <input
            type="text"
            placeholder="Buscar por nombre, dorsal o posición..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="form-input"
            style={{ paddingLeft: '2rem', fontSize: '0.82rem', height: '36px' }}
          />
        </div>

        {/* Advertencia si el seleccionado está en unidad contraria */}
        {isSelectedInOpposing && (
          <div
            style={{
              backgroundColor: 'var(--brand-yellow-subtle)',
              border: '1px solid var(--brand-yellow)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.5rem 0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontSize: '0.75rem',
              color: 'var(--text-main)',
              marginBottom: '0.5rem',
            }}
          >
            <AlertTriangle size={16} color="var(--brand-yellow)" />
            <span>
              <strong>Atención:</strong> {selectedAthlete?.full_name} ya está en la unidad contraria. Una persona no puede participar en ataque y defensa en el mismo snap.
            </span>
          </div>
        )}

        {/* Lista de atletas seleccionables */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--radius-sm)',
            minHeight: '160px',
            maxHeight: '220px',
          }}
        >
          {filteredAthletes.length === 0 ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
              No se encontraron atletas disponibles.
            </div>
          ) : (
            filteredAthletes.map((athlete) => {
              const isCurrent = athlete.id === currentSlot.athlete_id
              const isAlreadyInUnit = sameUnitAthleteIds.includes(athlete.id)
              const isInOpposing = opposingUnitAthleteIds.includes(athlete.id)
              const isSelected = athlete.id === selectedAthleteId
              const isDisabled = isCurrent || isAlreadyInUnit

              return (
                <div
                  key={athlete.id}
                  onClick={() => {
                    if (!isDisabled) setSelectedAthleteId(athlete.id)
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.55rem 0.75rem',
                    borderBottom: '1px solid var(--surface-border)',
                    backgroundColor: isSelected
                      ? 'var(--brand-red-subtle)'
                      : isDisabled
                      ? 'var(--surface-subtle)'
                      : 'var(--surface-card)',
                    opacity: isDisabled ? 0.55 : 1,
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.1s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        backgroundColor: isSelected
                          ? 'var(--brand-red)'
                          : 'var(--nav-bg-surface)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {athlete.dorsal_number || '—'}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-main)' }}>
                        {athlete.full_name}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        Pos: {athlete.primary_position || 'Polivalente'}
                        {isCurrent && ' (En esta posición)'}
                        {isAlreadyInUnit && ' (Ya en la unidad)'}
                        {isInOpposing && ' (En unidad contraria)'}
                      </div>
                    </div>
                  </div>

                  {isSelected && <UserCheck size={18} color="var(--brand-red)" />}
                </div>
              )
            })
          )}
        </div>

        {/* Botones de acción */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            marginTop: '1rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--surface-border)',
          }}
        >
          {isCustomLineup && onResetToTemplate ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                onResetToTemplate()
                onClose()
              }}
              style={{ fontSize: '0.78rem', padding: '0.45rem 0.75rem', minHeight: '40px' }}
              title="Restablecer este quinteto a la plantilla base original"
            >
              <RotateCcw size={14} />
              <span>Plantilla base</span>
            </button>
          ) : (
            <div />
          )}

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              style={{ fontSize: '0.82rem', minHeight: '44px', minWidth: '80px' }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!selectedAthleteId || isSelectedInOpposing}
              onClick={handleConfirm}
              style={{
                fontSize: '0.82rem',
                minHeight: '44px',
                minWidth: '120px',
                backgroundColor: 'var(--brand-red)',
              }}
            >
              Confirmar cambio
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
